import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { operations, SegmentsRequestBody, SegmentsSegmentIDsRequest } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import { mapSettledWithConcurrency } from '@/utils/concurrency';
import type { HookMutationConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { invalidateAllAudiences } from '../audience/cache';
import {
  invalidateSegmentLists,
  patchSegmentArchived,
  removeBulkUsageCache,
  removeSegmentCache,
  setSegmentCache,
} from './cache';
import { parseSegment, type Segment } from './entity';

/**
 * Segment write actions ported from the Ember `segment-actions` service and the
 * `segment` model. Request bodies are derived from the generated endpoint types
 * (`SegmentsSegmentIDsRequest`, the add-tags operation body) so they can't drift.
 *
 * The bulk archive/unarchive/delete endpoints all take `{ segment_ids: number[] }`
 * and return 204; the layer works in string ids, so they're coerced to numbers
 * at the wire boundary (matching Ember's `parseInt`).
 */

/* ------------------------------------------------------------------------- */
/* Plain fns (callable outside React)                                         */
/* ------------------------------------------------------------------------- */

function toIdsBody(segmentIds: readonly string[]): SegmentsSegmentIDsRequest {
  return { segment_ids: segmentIds.map(Number) };
}

/** @public */
export async function archiveSegments(workspaceId: number, segmentIds: string[]): Promise<void> {
  await hydraUiApi.segments.archive({
    params: { path: { environment_id: String(workspaceId) } },
    body: toIdsBody(segmentIds),
  });
}

/** @public */
export async function unarchiveSegments(workspaceId: number, segmentIds: string[]): Promise<void> {
  await hydraUiApi.segments.unarchive({
    params: { path: { environment_id: String(workspaceId) } },
    body: toIdsBody(segmentIds),
  });
}

/** @public */
export async function deleteSegments(workspaceId: number, segmentIds: string[]): Promise<void> {
  await hydraUiApi.segments.delete({
    params: { path: { environment_id: String(workspaceId) } },
    body: toIdsBody(segmentIds),
  });
}

// The add-tags body is a union: attach existing tags by `tag_ids`, or attach/create
// them by name via `tags`. This layer only sends the by-name shape, so pull that
// arm out of the generated operation body rather than restating the tag fields.
type AddSegmentTagsBody =
  operations['post-v1-environments-environment_id-segments-segment_id-tags']['requestBody']['content']['application/json'];
type SegmentTags = Extract<AddSegmentTagsBody, { tags: unknown }>['tags'];

/** @public */
export async function addSegmentTags(
  workspaceId: number,
  segmentId: string,
  tags: SegmentTags,
): Promise<Segment | null> {
  const res = await hydraUiApi.segments.tags({
    params: { path: { environment_id: String(workspaceId), segment_id: segmentId } },
    body: { tags },
  });
  return res.segment == null ? null : parseSegment(res.segment);
}

/**
 * Remove a tag from a segment. This is a POST (not DELETE) with the tag id in
 * the path and no body — matching Ember and the generated `tagsDelete` op.
 *
 * @public
 */
export async function removeSegmentTag(workspaceId: number, segmentId: string, tagId: number): Promise<Segment | null> {
  const res = await hydraUiApi.segments.tagsDelete({
    params: { path: { environment_id: String(workspaceId), segment_id: segmentId, tag_id: String(tagId) } },
  });
  return res.segment == null ? null : parseSegment(res.segment);
}

export type CreateSegmentInput = SegmentsRequestBody['segment'];

/** @public */
export async function createSegment(workspaceId: number, input: CreateSegmentInput): Promise<Segment> {
  const res = await hydraUiApi.segments.create({
    params: { path: { environment_id: String(workspaceId) } },
    body: { segment: input },
  });
  if (res.segment == null) {
    throw new Error('Segment create returned no segment');
  }
  return parseSegment(res.segment);
}

/**
 * The membership endpoint takes one profile per request, so adding a selection
 * fans out one PUT per `cio_id`, capped at 5 in flight. Results settle
 * independently — callers report the partial-failure tally.
 */
const ADD_MEMBERS_CONCURRENCY = 5;

export interface AddCustomersToManualSegmentInput {
  segmentId: string;
  /** Profile `cio_id`s (the membership endpoint's `customer_id`). */
  customerIds: readonly string[];
}

export interface AddCustomersToManualSegmentResult {
  total: number;
  failed: number;
}

/** @public */
export async function addCustomersToManualSegment(
  workspaceId: number,
  { segmentId, customerIds }: AddCustomersToManualSegmentInput,
): Promise<AddCustomersToManualSegmentResult> {
  const results = await mapSettledWithConcurrency(customerIds, ADD_MEMBERS_CONCURRENCY, (customerId) =>
    hydraUiApi.segments.members({
      params: { path: { environment_id: String(workspaceId), segment_id: segmentId } },
      body: { type: 'add', customer_id: customerId },
    }),
  );
  const failed = results.filter((r) => r.status === 'rejected').length;
  return { total: results.length, failed };
}

/* ------------------------------------------------------------------------- */
/* Mutation hooks                                                             */
/* ------------------------------------------------------------------------- */

interface SegmentMutationOptions<TVariables, TResult> {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<TResult, TVariables>;
}

/**
 * Shared scaffold for the segment mutation hooks: resolve the workspace, run the
 * action, apply the entity's cache updates, then call the caller's `onSuccess`.
 */
function useSegmentMutation<TVariables, TResult>(
  { workspaceId: override, mutationConfig }: SegmentMutationOptions<TVariables, TResult>,
  run: (workspaceId: number, variables: TVariables) => Promise<TResult>,
  updateCache: (qc: QueryClient, workspaceId: number, variables: TVariables, result: TResult) => void,
) {
  const workspaceId = override ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: TVariables) => run(workspaceId, variables),
    onSuccess: (result, variables, ...args) => {
      updateCache(qc, workspaceId, variables, result);
      onSuccess?.(result, variables, ...args);
    },
  });
}

type BulkVariables = { segmentIds: string[] };

/** @public */
export const useArchiveSegments = (options: SegmentMutationOptions<BulkVariables, void> = {}) =>
  useSegmentMutation(
    options,
    (workspaceId, { segmentIds }) => archiveSegments(workspaceId, segmentIds),
    (qc, workspaceId, { segmentIds }) => {
      patchSegmentArchived(qc, workspaceId, segmentIds, true);
      invalidateSegmentLists(qc, workspaceId);
      // Archiving a segment pauses its associated audiences server-side.
      invalidateAllAudiences(qc, workspaceId);
    },
  );

/** @public */
export const useUnarchiveSegments = (options: SegmentMutationOptions<BulkVariables, void> = {}) =>
  useSegmentMutation(
    options,
    (workspaceId, { segmentIds }) => unarchiveSegments(workspaceId, segmentIds),
    (qc, workspaceId, { segmentIds }) => {
      patchSegmentArchived(qc, workspaceId, segmentIds, false);
      invalidateSegmentLists(qc, workspaceId);
    },
  );

/** @public */
export const useDeleteSegments = (options: SegmentMutationOptions<BulkVariables, void> = {}) =>
  useSegmentMutation(
    options,
    (workspaceId, { segmentIds }) => deleteSegments(workspaceId, segmentIds),
    (qc, workspaceId, { segmentIds }) => {
      for (const id of segmentIds) {
        removeSegmentCache(qc, workspaceId, id);
      }
      // Bulk-usage entries are keyed by id set, so drop that subtree separately.
      removeBulkUsageCache(qc, workspaceId);
      invalidateSegmentLists(qc, workspaceId);
      // Deleting a segment deletes its associated audiences server-side.
      invalidateAllAudiences(qc, workspaceId);
    },
  );

// Both tag ops return the updated segment; cache it and refresh lists.
function cacheSegmentResult(qc: QueryClient, workspaceId: number, _variables: unknown, segment: Segment | null): void {
  if (segment != null) {
    setSegmentCache(qc, workspaceId, segment);
  }
  invalidateSegmentLists(qc, workspaceId);
}

/** @public */
export const useAddSegmentTags = (
  options: SegmentMutationOptions<{ segmentId: string; tags: SegmentTags }, Segment | null> = {},
) =>
  useSegmentMutation(
    options,
    (workspaceId, { segmentId, tags }) => addSegmentTags(workspaceId, segmentId, tags),
    cacheSegmentResult,
  );

/** @public */
export const useRemoveSegmentTag = (
  options: SegmentMutationOptions<{ segmentId: string; tagId: number }, Segment | null> = {},
) =>
  useSegmentMutation(
    options,
    (workspaceId, { segmentId, tagId }) => removeSegmentTag(workspaceId, segmentId, tagId),
    cacheSegmentResult,
  );

/** @public */
export const useCreateSegment = (options: SegmentMutationOptions<CreateSegmentInput, Segment> = {}) =>
  useSegmentMutation(options, createSegment, (qc, workspaceId, _variables, segment) => {
    setSegmentCache(qc, workspaceId, segment);
    invalidateSegmentLists(qc, workspaceId);
  });

/**
 * No automatic cache invalidation: the mutation resolves even when every PUT
 * failed (the tally reports it), so callers decide what a "success" refresh
 * means. Membership counts update asynchronously server-side anyway.
 *
 * @public
 */
export const useAddCustomersToManualSegment = (
  options: SegmentMutationOptions<AddCustomersToManualSegmentInput, AddCustomersToManualSegmentResult> = {},
) => useSegmentMutation(options, addCustomersToManualSegment, () => {});
